import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Package, ReceiptEuro, Users, Building2,
  Truck, Car, Settings, Bell, ScrollText, LogOut, Menu, X, Sparkles, Euro, Gift, ServerCog, Store,
  MessageSquare, CheckCircle2, XCircle, AlertTriangle, PhoneCall, UserPlus, CheckCheck, Trash2, UserCog, Globe, Calculator, Headset, FileSignature, Map as MapIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const MENU = [
  { to: "/dashboard", icon: LayoutDashboard, key: "menu.dashboard", roles: ["admin", "support", "partner", "driver", "client"] },
  { to: "/quotes", icon: FileText, key: "menu.quotes", roles: ["admin", "support"] },
  { to: "/manual-quote", icon: Calculator, key: "menu.manualQuote", roles: ["admin", "support"] },
  { to: "/orders", icon: Package, key: "menu.orders", roles: ["admin", "support", "partner", "driver", "client"] },
  { to: "/logistics", icon: Sparkles, key: "menu.logistics", roles: ["admin", "support"] },
  { to: "/invoices", icon: ReceiptEuro, key: "menu.invoices", roles: ["admin", "support", "partner", "client"] },
  { to: "/financial", icon: Euro, key: "menu.financial", roles: ["admin", "support"] },
  { to: "/referrals", icon: Gift, key: "menu.referrals", roles: ["admin", "support", "partner"] },
  { to: "/clients", icon: Users, key: "menu.clients", roles: ["admin", "support"] },
  { to: "/partners", icon: Building2, key: "menu.partners", roles: ["admin", "support"] },
  { to: "/partners-map", icon: MapIcon, key: "menu.partnersMap", roles: ["admin", "support"] },
  { to: "/company", icon: Building2, key: "menu.company", roles: ["partner"] },
  { to: "/drivers", icon: Truck, key: "menu.drivers", roles: ["admin", "support", "partner"] },
  { to: "/vehicles", icon: Car, key: "menu.vehicles", roles: ["admin", "support", "partner"] },
  { to: "/marketplace", icon: Store, key: "menu.marketplace", roles: ["admin", "support", "partner"], flag: "marketplace_enabled" },
  { to: "/settings", icon: Settings, key: "menu.settings", roles: ["admin"] },
  { to: "/seo", icon: Globe, key: "menu.seo", roles: ["admin", "support"] },
  { to: "/system", icon: ServerCog, key: "menu.system", roles: ["admin"] },
  { to: "/audit", icon: ScrollText, key: "menu.audit", roles: ["admin", "support"] },
];

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [flags, setFlags] = useState({});
  const [branding, setBranding] = useState({});
  const [chatUnread, setChatUnread] = useState(0);
  const [clientActive, setClientActive] = useState(null);
  const [clientDocsMissing, setClientDocsMissing] = useState(0);

  const items = MENU.filter((m) => m.roles.includes(user?.role)).filter((m) => !m.flag || flags[m.flag]);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) { logError("layout:notifications", e); }
  };
  useEffect(() => { if (user?.role === "client") return; loadNotifs(); const id = setInterval(loadNotifs, 20000); return () => clearInterval(id); }, [user]);
  useEffect(() => { api.get("/feature-flags").then(({ data }) => setFlags(data || {})).catch((e) => logError("layout:feature-flags", e)); }, []);
  useEffect(() => { api.get("/public/settings").then(({ data }) => setBranding(data?.branding || {})).catch((e) => logError("layout:public-settings", e)); }, []);
  useEffect(() => {
    if (!["partner", "driver"].includes(user?.role)) return;
    const fetchUnread = () => api.get("/chat/unread-count").then(({ data }) => setChatUnread(data.count || 0)).catch((e) => logError("layout:chat-unread", e));
    fetchUnread();
    const id = setInterval(fetchUnread, 10000);
    return () => clearInterval(id);
  }, [user]);
  useEffect(() => {
    if (user?.role !== "client") return;
    api.get("/orders").then(({ data }) => {
      const act = (data || []).find((o) => ["pending", "confirmed", "in_transit"].includes(o.status)) || null;
      setClientActive(act);
      if (act?.id) api.get(`/documents/required/${act.id}`)
        .then(({ data }) => setClientDocsMissing(data?.complete ? 0 : (data?.missing?.length || 0)))
        .catch((e) => logError("layout:client-docs", e));
    }).catch((e) => logError("layout:client-orders", e));
  }, [user]);
  const unread = notifs.filter((n) => !n.read).length;

  const NOTIF_ICON = {
    new_message: MessageSquare, new_order: Package, partner_accepted: CheckCircle2,
    partner_refused: XCircle, driver_incident: AlertTriangle, document_uploaded: ScrollText,
    document_approved: CheckCircle2, payment_received: Euro, callback_request: PhoneCall,
    new_client_registration: UserPlus,
  };
  const openNotif = async (n) => {
    try { if (!n.read && n.id) await api.post("/notifications/read", { ids: [n.id] }); } catch (e) { logError("layout:notif-read", e); }
    loadNotifs();
    if (n.link) navigate(n.link);
  };
  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); } catch (e) { logError("layout:notif-read-all", e); }
    loadNotifs();
  };
  const deleteNotif = async (e, n) => {
    e.stopPropagation();
    if (!n.id) return;
    try { await api.delete(`/notifications/${n.id}`); } catch (err) { logError("layout:notif-delete", err); }
    loadNotifs();
  };
  const deleteRead = async () => {
    try { await api.post("/notifications/delete-read"); } catch (e) { logError("layout:notif-delete-read", e); }
    loadNotifs();
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white flex flex-col transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="dashboard-sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <Link to="/" className="flex items-center" data-testid="sidebar-logo">
            {branding.compact_logo ? (
              <span className="bg-white/95 rounded-md px-2 py-1"><img src={branding.compact_logo} alt="Rimpatrio24" className="h-8 w-auto object-contain" /></span>
            ) : (
              <span className="font-display text-xl font-bold tracking-tight">Rimpatrio<span className="text-blue-400">24</span></span>
            )}
          </Link>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {items.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${m.to.slice(1)}`}
              className={({ isActive }) => `r24-sidebar-link ${isActive ? "active" : ""}`}
            >
              <m.icon size={18} strokeWidth={1.5} />
              {t(m.key)}
              {m.to === "/orders" && chatUnread > 0 && (
                <span className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="chat-unread-badge">{chatUnread}</span>
              )}
            </NavLink>
          ))}
          {user?.role === "client" && (
            <>
              <NavLink
                to={clientActive ? `/orders/${clientActive.id}?tab=chat` : "/orders"}
                onClick={() => setOpen(false)}
                data-testid="nav-client-support"
                className="r24-sidebar-link"
              >
                <Headset size={18} strokeWidth={1.5} />
                {t("menu.support247")}
              </NavLink>
              <NavLink
                to={clientActive ? `/orders/${clientActive.id}?tab=documents` : "/orders"}
                onClick={() => setOpen(false)}
                data-testid="nav-client-docs-missing"
                className="r24-sidebar-link !text-rose-300 hover:!text-white hover:!bg-rose-600/80"
              >
                <AlertTriangle size={18} strokeWidth={1.5} />
                {t("menu.docsMissing")}
                {clientDocsMissing > 0 && (
                  <span className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center">{clientDocsMissing}</span>
                )}
              </NavLink>
              {clientActive && clientActive.payment_status === "paid" && clientActive.mandate_status && clientActive.mandate_status !== "signed" && (
                <NavLink
                  to={`/orders/${clientActive.id}?tab=mandate`}
                  onClick={() => setOpen(false)}
                  data-testid="nav-client-mandate"
                  className="r24-sidebar-link !text-rose-300 hover:!text-white hover:!bg-rose-600/80"
                >
                  <FileSignature size={18} strokeWidth={1.5} />
                  {t("menu.mandate")}
                  <span className="ml-auto h-2 w-2 rounded-full bg-rose-500 animate-pulse" />
                </NavLink>
              )}
            </>
          )}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-slate-400">
          <div className="capitalize">{user?.role}</div>
          <div className="truncate">{user?.email}</div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="h-16 sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center px-4 lg:px-6 gap-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu size={22} />
          </button>
          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitcher />
            {user?.role !== "client" && (
            <DropdownMenu onOpenChange={(o) => o && loadNotifs()}>
              <DropdownMenuTrigger asChild>
                <button className="relative rounded-lg p-2 hover:bg-slate-100" data-testid="notifications-bell">
                  <Bell size={18} strokeWidth={1.5} />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <DropdownMenuLabel className="p-0">{t("menu.notifications")}</DropdownMenuLabel>
                  <div className="flex items-center gap-2">
                    {unread > 0 && (
                      <button onClick={markAllRead} data-testid="notifications-mark-all-read"
                        className="text-[11px] text-blue-700 hover:underline flex items-center gap-1">
                        <CheckCheck size={13} /> {t("notif.markAllRead")}
                      </button>
                    )}
                    {notifs.some((n) => n.read) && (
                      <button onClick={deleteRead} data-testid="notifications-delete-read"
                        className="text-[11px] text-slate-500 hover:text-red-600 hover:underline flex items-center gap-1">
                        <Trash2 size={13} /> {t("notif.clearRead")}
                      </button>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <div className="px-3 py-4 text-sm text-slate-400">{t("common.none")}</div>}
                {notifs.slice(0, 10).map((n) => {
                  const Icon = NOTIF_ICON[n.category] || Bell;
                  return (
                    <div key={n.id} data-testid="notification-item"
                      className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 flex gap-2.5 items-start hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/40"}`}>
                      <button onClick={() => openNotif(n)} className="flex gap-2.5 min-w-0 flex-1 text-left">
                        <Icon size={16} className="mt-0.5 shrink-0 text-blue-900" strokeWidth={1.5} />
                        <span className="min-w-0">
                          <span className="font-medium block truncate">{n.title}</span>
                          <span className="text-slate-500 text-xs block truncate">{n.body}</span>
                        </span>
                      </button>
                      <button onClick={(e) => deleteNotif(e, n)} data-testid="notification-delete"
                        className="shrink-0 text-slate-300 hover:text-red-600 mt-0.5" title={t("common.delete")}>
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg p-1 pr-2 hover:bg-slate-100" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-900 text-white text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[120px] truncate">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="cursor-pointer" data-testid="account-menu-item">
                  <Link to="/account"><UserCog size={16} className="mr-2" /> {t("account.title")}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="cursor-pointer text-red-600">
                  <LogOut size={16} className="mr-2" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        {open && <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
